import './DraggableMarketCharacteristicDimensions.scss';
import React from "react";
import classnames from "classnames";
import {RowColConfig} from "@/components/DodConfigEditor/types";
import {DragDropContext, Droppable, DropResult} from "react-beautiful-dnd";
import {
    DraggableProductDimension
} from "@/components/DodConfigEditor/builders/DodLayoutBuilder/DodDesignYourLayout/DraggableProductDimension";
import {useEventDataWithUserInfo, useTrackEvent} from "@/analytics/AnalyticsContext";
import {useDodWizard} from "@/components/DodConfigEditor/DodRunConfigWizard/DodWizardContext";
import {caseInsensitiveSort, caseInsensitiveReverseSort} from "@/utils/Sort";

export interface DraggableMarketCharacteristicDimensionsProps {
    className?: string;
    dimensions: RowColConfig[];
    mode?: string|null;
    onChange?(dimensions: RowColConfig[]);
}

const baseClassName = 'draggable-market-characteristic-dimensions';

export function DraggableMarketCharacteristicDimensions({className, dimensions, onChange, mode}: DraggableMarketCharacteristicDimensionsProps) {

    const trackEvent = useTrackEvent();
    const getEventData = useEventDataWithUserInfo();
    const { runConfig } = useDodWizard();

    // Market characteristics are never stackable, unlike products
    const hideStack = true;

    function handleDragEnd(result: DropResult) {

        if (!result.destination) {
            return;
        }

        const from = result.source.index;
        const to = result.destination.index;
        const clonedDimensions = [...dimensions];
        const [moved] = clonedDimensions.splice(from, 1);
        clonedDimensions.splice(to, 0, moved);

        onChange?.([...clonedDimensions]);

        trackEvent({
            type: 'click',
            name: 'dod_layout_value_reorder',
            data: getEventData({ dodWizardStep: 'layout', filter: 'market_characteristic' }),
        });
    }

    function handleDimensionChange(config: RowColConfig) {
        // If sort type changed, compute sorted values for 'asc' and 'desc'
        let updatedConfig = config;
        
        if (config.sortType && config.sortType !== 'manual') {
            // Get the market characteristic filter to access its values
            const mcFilter = Object.values(runConfig.filters.marketCharacteristics || {})
                .find(f => f.displayName === config.dim);
            
            if (mcFilter && mcFilter.values !== 'all' && Array.isArray(mcFilter.values)) {
                let sortedValues: string[] = [];
                
                if (config.sortType === 'asc') {
                    sortedValues = [...(mcFilter.values || [])].sort(caseInsensitiveSort);
                } else if (config.sortType === 'desc') {
                    sortedValues = [...(mcFilter.values || [])].sort(caseInsensitiveReverseSort);
                }
                
                updatedConfig = {
                    ...config,
                    values: [...sortedValues, ...mcFilter.summedSelections.map(ss => ss.name)]
                };
            }
        }
        
        // Single-sort behavior: if sorting is enabled on one field, reset others to 'manual'
        const updatedDimensions = dimensions.map(d => {
            if (d.dim === config.dim) {
                return updatedConfig;
            }
            // If the changed config has a non-manual sortType, reset other fields to manual
            if (config.sortType && config.sortType !== 'manual') {
                return { ...d, sortType: 'manual' as const };
            }
            return d;
        });
        onChange?.(updatedDimensions);
    }

    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="droppable-market-characteristics">
                {(provided) => (
                    <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className={classnames(baseClassName, className)}
                    >
                        {dimensions.map((config, index) => (
                            <DraggableProductDimension
                                config={config}
                                index={index}
                                key={config.dim}
                                label={config.dim}
                                hideStack={hideStack}
                                hideHideOption={true}
                                onConfigChange={handleDimensionChange}
                                mode={mode}
                            />
                        ))}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        </DragDropContext>
    );
}
